import { formatDateOnly, isNeedsReview } from '@dataroom/shared';
import type {
  BulkReclassifyRequest,
  BulkReclassifyResponse,
  CategoryNode,
  ClassificationProposal,
  ClassificationStatus,
  ReviewableClassificationStatus,
  RoomDocument,
} from '@dataroom/shared';
import {
  categoryNodesById,
  deepestCategoryId,
  isInstitutionLevel,
  placeholderDisplayName,
  SYNTHETIC_CATEGORY_IDS,
} from '@dataroom/shared';

/**
 * "Needs review" = the classification states the admin should look at: the AI
 * finished without filing the document at a specific home (`low_confidence` —
 * it may have filed the tiers it was sure of), or could not classify the file
 * at all (`failed`). Both open the same review surface from the Data room
 * list and the upload status screen, and both are what the bulk "Re-run AI"
 * re-enqueues (ADR-0018 §6).
 */
export const NEEDS_REVIEW_STATUSES: readonly ReviewableClassificationStatus[] = [
  'low_confidence',
  'failed',
];

export { isNeedsReview };

export interface NeedsReviewCounts {
  lowConfidence: number;
  failed: number;
  total: number;
}

/** Count the needs-review documents in a loaded list. The room listing is not
 * paginated (GET /rooms/:roomId/documents returns every visible document), so a
 * client-side count over it is the room's true count. */
export function countNeedsReview(
  docs: readonly { classificationStatus?: ClassificationStatus }[],
): NeedsReviewCounts {
  let lowConfidence = 0;
  let failed = 0;
  for (const d of docs) {
    if (d.classificationStatus === 'low_confidence') lowConfidence += 1;
    else if (d.classificationStatus === 'failed') failed += 1;
  }
  return { lowConfidence, failed, total: lowConfidence + failed };
}

export interface BulkReclassifyOutcome {
  /** Documents re-enqueued across every call. */
  enqueued: number;
  /** True when any status selector matched more than the server's 500 cap. */
  truncated: boolean;
}

/**
 * Re-run classification on every needs-review document: one bulk call per
 * status that currently has documents (`{status:'low_confidence'}` and/or
 * `{status:'failed'}`). The server selects by status itself, so the request is
 * correct even if the client list is a moment stale, and each selector carries
 * its own 500 cap. `post` is injected so this stays a pure, testable unit.
 */
export async function reclassifyNeedsReview(
  post: (body: BulkReclassifyRequest) => Promise<BulkReclassifyResponse>,
  counts: Pick<NeedsReviewCounts, 'lowConfidence' | 'failed'>,
): Promise<BulkReclassifyOutcome> {
  const statuses = NEEDS_REVIEW_STATUSES.filter((s) =>
    s === 'low_confidence' ? counts.lowConfidence > 0 : counts.failed > 0,
  );
  const results = await Promise.all(statuses.map((status) => post({ status })));
  return {
    enqueued: results.reduce((n, r) => n + r.enqueued, 0),
    truncated: results.some((r) => r.truncated),
  };
}

/** One choice in a review round: a child of the deepest filed category. */
export interface ReviewOption {
  id: string;
  name: string;
  /** It has categories below it — choosing it opens another round. */
  hasChildren: boolean;
  /** The AI's pending suggestion for this category, if it made one. */
  suggestion: ClassificationProposal | null;
}

export interface ReviewRound {
  /** The deepest filed category the round continues from; null = the top level. */
  parentId: string | null;
  options: ReviewOption[];
}

/**
 * The review round (ADR-0019 update 2026-10-02): the AI attached the tiers it
 * was sure of, so the person continues from the DEEPEST filed category and
 * chooses among its children — one tier per round — until the document is filed
 * at a specific home (the server then reads it `classified`). With nothing filed
 * the round is the top level. The AI's next-level suggestion, if any, is marked
 * and listed first. Null when the document needs no round (classified or
 * processing) or the deepest category has no children.
 */
export function reviewRound(
  doc: Pick<RoomDocument, 'categoryTagIds' | 'classificationStatus'>,
  tree: readonly CategoryNode[],
  parentOf: ReadonlyMap<string, string | null>,
  proposals: readonly ClassificationProposal[],
  /** A level the person stepped back up to with the breadcrumb (lib/filing-trail):
   * its parent (null = the top level), and — for a checklist phase — only that
   * phase's top-level categories. Omitted = continue from the deepest filed one. */
  from?: { parentId: string | null; onlyIds?: ReadonlySet<string> },
): ReviewRound | null {
  const status = doc.classificationStatus;
  // Deal paperwork (`not_classifiable`) is never filed: the server refuses it.
  if (status !== 'low_confidence' && status !== 'failed') {
    return null;
  }
  const parentId = from ? from.parentId : deepestCategoryId(doc.categoryTagIds, parentOf);
  const children = (
    parentId
      ? (categoryNodesById(tree).get(parentId)?.children ?? [])
      : tree.filter((n) => !SYNTHETIC_CATEGORY_IDS.has(n.id))
  ).filter((n) => !from?.onlyIds || from.onlyIds.has(n.id));
  if (children.length === 0) return null;
  const suggested = new Map(proposals.map((p) => [p.tagId, p] as const));
  // A placeholder ("[Credit Card]", "[State]") stands for its accounts or states:
  // never a place to file (the server refuses it, owner rule 2026-10-12). The
  // account tree's "[Institution]" level is no place to file either, but what
  // is under it is: the statement types of accounts whose bank is not known yet,
  // offered as "Bank not identified › Bank Statements" (review 2026-10-15 —
  // hiding the level hid those accounts).
  const options = children
    .flatMap((child) =>
      isInstitutionLevel(child.name)
        ? child.children.map((type) => ({
            ...type,
            name: `${placeholderDisplayName(child.name)} › ${type.name}`,
          }))
        : [child],
    )
    .filter((child) => !isPlaceholderName(child.name))
    .map((child) => ({
      id: child.id,
      name: child.name,
      hasChildren: child.children.length > 0,
      suggestion: suggested.get(child.id) ?? null,
    }));
  options.sort((a, b) => (b.suggestion?.confidence ?? -1) - (a.suggestion?.confidence ?? -1));
  return { parentId, options };
}

/** "[Credit Card]", "[Account Name]", "[State]" — a checklist placeholder, not a category. */
export function isPlaceholderName(name: string): boolean {
  return /^\s*\[[^[\]]+\]\s*$/.test(name);
}

/** A readable label for a pending suggestion that is not one of the round's options. */
export function suggestionLabel(
  proposal: Pick<ClassificationProposal, 'tagId' | 'tagName' | 'tagType'>,
  categoryPaths: ReadonlyMap<string, string>,
): string {
  const path = categoryPaths.get(proposal.tagId);
  if (path) return path;
  const name = proposal.tagName ?? 'Suggested tag';
  switch (proposal.tagType) {
    case 'date':
      return `Date: ${formatDateOnly(name, name)}`;
    case 'doc_type':
      return `Document type: ${name}`;
    case 'category':
      return `New category: ${name}`;
    default:
      return name; // a period tag is already named "Period: Annual"
  }
}

/**
 * Whether a FILED document still has a review step: its date of data or its
 * period is only a pending AI suggestion (owner 2026-10-12: "after I selected
 * the account it never came back to me for a date"). Filing the category used to
 * drop the document out of the queue with its date suggestion unseen.
 */
export function awaitsDateOrPeriod(
  doc: Pick<RoomDocument, 'id' | 'classificationStatus' | 'dataDate' | 'period'>,
  proposals: readonly Pick<ClassificationProposal, 'documentId' | 'tagType' | 'tagName'>[],
): boolean {
  if (doc.classificationStatus !== 'classified') return false;
  return proposals.some(
    (p) =>
      p.documentId === doc.id &&
      ((p.tagType === 'date' && !doc.dataDate) ||
        (p.tagType === 'custom' && (p.tagName ?? '').startsWith('Period: ') && !doc.period)),
  );
}

/**
 * The full-page review queue, in listing order: every document that still
 * needs FILING (needs-review), plus the document being shown while its own date
 * or period is still only a suggestion ({@link awaitsDateOrPeriod} — owner
 * 2026-10-12: filing the category must come back to him for the date).
 *
 * Another document that is already filed is never queued just because its date
 * is a pending suggestion (owner 2026-10-15: "it should go to the next document
 * that needs classification" — not the next one in the list that is already
 * classified properly).
 */
export function reviewQueue<
  T extends Pick<RoomDocument, 'id' | 'classificationStatus' | 'dataDate' | 'period'>,
>(
  docs: readonly T[],
  proposals: readonly Pick<ClassificationProposal, 'documentId' | 'tagType' | 'tagName'>[] = [],
  currentId: string | null = null,
): T[] {
  return docs.filter(
    (d) =>
      isNeedsReview(d.classificationStatus) ||
      (d.id === currentId && awaitsDateOrPeriod(d, proposals)),
  );
}

/** How many documents still need filing — the page's "N left to file". */
export function leftToFile(
  docs: readonly { classificationStatus?: ClassificationStatus }[],
): number {
  return docs.filter((d) => isNeedsReview(d.classificationStatus)).length;
}

/**
 * Which document the review page shows, from the FRESH listing (call it after
 * the listing has been re-fetched): the current document while it is still in
 * the queue; otherwise the next queued document AFTER the current one's place
 * in the listing, wrapping round to earlier ones. `anchor` is the current
 * document's last known listing position, used when it has left the listing.
 * Null when nothing is left to file — never an already-filed document.
 */
export function pickReviewDocument<T extends { id: string }>(
  listing: readonly T[],
  queue: readonly T[],
  currentId: string | null,
  anchor: number,
): T | null {
  if (queue.length === 0) return null;
  const here = currentId ? queue.find((d) => d.id === currentId) : undefined;
  if (here) return here;
  const queued = new Set(queue.map((d) => d.id));
  const at = currentId ? listing.findIndex((d) => d.id === currentId) : -1;
  // Still listed: start just after it. Gone: the documents after it moved up
  // one, so the next one now sits at its old position.
  const start = at !== -1 ? at + 1 : anchor < listing.length ? Math.max(0, anchor) : 0;
  for (let i = 0; i < listing.length; i += 1) {
    const d = listing[(start + i) % listing.length];
    if (d && queued.has(d.id)) return d;
  }
  return queue[0] ?? null;
}
